// client/src/pages/admin/adminMessaging.jsx

import React, { useState, useEffect, useContext } from 'react';
import { ConversationModal, AnnouncementDetailModal } from '../../modals';
import { useNavigate } from 'react-router-dom';
import ProfileHeader from '../../components/headers/profileHeader';
import SharedHeader from '../../components/headers/sharedHeader1';
import showNotification from '../../utilities/showNotification';
import BackButton from '../../components/customs/backButton';
import { GlobalContext } from '../../context/provider';
import useStudent from '../../hooks/useStudent';
import useInstructor from '../../hooks/useInstructor';
import useAnnouncement from '../../hooks/useAnnouncement';

import '../../styles/globalstyles.css';
import './adminMessaging.css';

const AdminMessaging = () => {
  const navigate = useNavigate();
  const { authState: { authData } } = useContext(GlobalContext);

  const { getAllStudents } = useStudent();
  const { getAllInstructors } = useInstructor();
  const { sendAdminAnnouncement, getAnnouncementHistory } = useAnnouncement();

  const [isLoading, setIsLoading] = useState(true);
  const [students, setStudents] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [announcementHistory, setAnnouncementHistory] = useState([]);
  const [sendingAnnouncement, setSendingAnnouncement] = useState(false);

  // Compose modal
  const [showComposeModal, setShowComposeModal] = useState(false);
  const [recipientType, setRecipientType] = useState('all');
  const [announcementPriority, setAnnouncementPriority] = useState('medium');
  const [messageData, setMessageData] = useState({ title: '', body: '' });

  // Detail modal
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [studentsRes, instructorsRes] = await Promise.all([
        getAllStudents(),
        getAllInstructors()
      ]);
      if (studentsRes.success) setStudents(studentsRes.data || []);
      if (instructorsRes.success) setInstructors(instructorsRes.data || []);
      await loadAnnouncementHistory();
    } catch (error) {
      showNotification('Failed to load users', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const loadAnnouncementHistory = async () => {
    try {
      const response = await getAnnouncementHistory(authData.id);
      if (response.success) setAnnouncementHistory(response.announcements || []);
    } catch (error) {
      console.error('Failed to load announcement history:', error);
    }
  };

  const handleSendBroadcast = async () => {
    if (!messageData.title || !messageData.body) {
      showNotification('Please fill in all fields', 'error');
      return;
    }
    setSendingAnnouncement(true);
    try {
      let targetUserIds = [];
      if (recipientType === 'students')     targetUserIds = students.map(s => s.loginId);
      else if (recipientType === 'instructors') targetUserIds = instructors.map(i => i.loginId);
      else targetUserIds = [...students.map(s => s.loginId), ...instructors.map(i => i.loginId)];

      const response = await sendAdminAnnouncement({
        adminId: authData.id,
        targetUserIds,
        title: messageData.title,
        body: messageData.body,
        priority: announcementPriority
      });

      if (response.success) {
        showNotification(`Sent to ${response.sentCount || targetUserIds.length} users`, 'success');
        setMessageData({ title: '', body: '' });
        setShowComposeModal(false);
        await loadAnnouncementHistory();
      } else {
        showNotification(response.error || 'Failed to send', 'error');
      }
    } catch (error) {
      showNotification('An error occurred', 'error');
    } finally {
      setSendingAnnouncement(false);
    }
  };

  const getPriorityConfig = (priority) => ({
    urgent: { color: '#ef4444', bg: 'rgba(239,68,68,0.1)',   label: '🚨 Urgent' },
    high:   { color: '#f59e0b', bg: 'rgba(245,158,11,0.1)',  label: '🔴 High'   },
    medium: { color: '#3b82f6', bg: 'rgba(59,130,246,0.1)',  label: '🔵 Medium' },
    low:    { color: '#10b981', bg: 'rgba(16,185,129,0.1)',  label: '🟢 Low'    },
  }[priority] || { color: '#3b82f6', bg: 'rgba(59,130,246,0.1)', label: '🔵 Medium' });

  const totalRecipients  = students.length + instructors.length;
  const totalAnnouncements = announcementHistory.length;
  const urgentCount = announcementHistory.filter(a => a.priority === 'urgent' || a.priority === 'high').length;
  const recentCount = announcementHistory.filter(a =>
    (new Date() - new Date(a.createdAt)) < 7 * 24 * 60 * 60 * 1000
  ).length;

  const profileHeaderData = { 
    ...authData, 
    // title: '📢 Announcements & Communication' 
  };

  return (
    <div className="ai-notebook-page" style={{height: '94vh'}}>
      {/* <div className="main-content"> */}
      {/* Header */}
      <div className="header-row">
        <ProfileHeader data={profileHeaderData} activePage="admin-messaging" />
        <div className="headerMiddle">
          <img src="/images/prof_ix.png" alt="Logo" className="header-logo" />
        </div>
        <SharedHeader notificationCount={0} icons={['home', 'back']} />
      </div>

      {/* Toolbar */}
      <div className="ai-notebook-toolbar">
        <div className="ai-notebook-toolbar-left">
          <BackButton 
            to="/admin/dashboard"
            activeTab="management"
            label="←"
            bgColor="#6b7280"
          />
          <span className="ai-notebook-flow-name">
            <span className="ai-notebook-lab-tag">📢 Admin Announcements & Communication</span>
            {/* <span className="ai-notebook-session-id">📚 Course: {courseInfo?.title} ({courseInfo?.courseId})</span> */}
          </span>
        </div>
        {/* <div className="ai-notebook-toolbar-right">
        </div> */}
      </div>

      <div className="ai-notebook-content">
        {isLoading ? (
          <div className="loading-container">
            <div className="loader"></div>
            <p>Loading...</p>
          </div>
        ) : (
          <>
            {/* ── Stats Row ── */}
            <div className="courses-grid">
              <div className="am-stat-card am-stat-users">
                <div className="am-stat-icon-wrap"><span className="am-stat-icon">👥</span></div>
                <div className="am-stat-info">
                  <div className="am-stat-number">{totalRecipients}</div>
                  <div className="am-stat-label">Total Users</div>
                  <div className="am-stat-sub">{students.length} students · {instructors.length} instructors</div>
                </div>
              </div>
              <div className="am-stat-card am-stat-sent">
                <div className="am-stat-icon-wrap"><span className="am-stat-icon">📨</span></div>
                <div className="am-stat-info">
                  <div className="am-stat-number">{totalAnnouncements}</div>
                  <div className="am-stat-label">Announcements Sent</div>
                  <div className="am-stat-sub">All time</div>
                </div>
              </div>
              <div className="am-stat-card am-stat-urgent">
                <div className="am-stat-icon-wrap"><span className="am-stat-icon">🚨</span></div>
                <div className="am-stat-info">
                  <div className="am-stat-number">{urgentCount}</div>
                  <div className="am-stat-label">High Priority</div>
                  <div className="am-stat-sub">Urgent or high</div>
                </div>
              </div>
              <div className="am-stat-card am-stat-recent">
                <div className="am-stat-icon-wrap"><span className="am-stat-icon">🕐</span></div>
                <div className="am-stat-info">
                  <div className="am-stat-number">{recentCount}</div>
                  <div className="am-stat-label">This Week</div>
                  <div className="am-stat-sub">Last 7 days</div>
                </div>
              </div>
            </div>

            {/* ── History Header + Compose Button ── */}
            <div className="nb-header" style={{color: 'white'}}>
              {/* <div className="am-history-title"> */}
              <h3>📋 Announcement History</h3>
              <span className="am-history-count">{totalAnnouncements} total</span>
              {/* </div> */}
              <button className="am-compose-btn" onClick={() => setShowComposeModal(true)}>
                <span className="am-compose-plus">+</span>
                New Announcement
              </button>
            </div>

            {/* ── Announcement History List ── */}
            <div className="nb-inner-wrap">
              {announcementHistory.length > 0 ? (
                announcementHistory.map((announcement, idx) => {
                  const config = getPriorityConfig(announcement.priority);
                  return (
                    <div
                      key={idx}
                      className="am-announcement-card"
                      onClick={() => setSelectedAnnouncement(announcement)}
                      style={{ '--priority-color': config.color, '--priority-bg': config.bg }}
                    >
                      <div className="am-card-priority-bar" style={{ background: config.color }} />
                      <div className="am-card-body">
                        <div className="am-card-top">
                          <h4 className="am-card-title">{announcement.title}</h4>
                          <span className="am-card-priority-badge"
                            style={{ color: config.color, background: config.bg }}>
                            {config.label}
                          </span>
                        </div>
                        <p className="am-card-preview">{announcement.body}</p>
                        <div className="am-card-footer">
                          <span className="am-card-meta">📨 {announcement.recipients?.length || 0} recipients</span>
                          <span className="am-card-meta">
                            🕐 {new Date(announcement.createdAt).toLocaleDateString('en-US', {
                              month: 'short', day: 'numeric', year: 'numeric',
                              hour: '2-digit', minute: '2-digit'
                            })}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="am-empty-state">
                  <div className="am-empty-icon">📭</div>
                  <h4>No announcements yet</h4>
                  <p>Click + to send your first announcement</p>
                  <button className="am-compose-btn" onClick={() => setShowComposeModal(true)}>
                    <span className="am-compose-plus">+</span> New Announcement
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </div>
      {/* </div> */}

      {/* ── Compose Modal (prebuilt) ── */}
      {showComposeModal && (
        <div className="modal-overlay" onClick={() => setShowComposeModal(false)}>
          <div className="modal-content-top" onClick={e => e.stopPropagation()}>
            <div className="modal-header-top">
              <h2>📢 New Announcement</h2>
              <button className="close-button" onClick={() => setShowComposeModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <ConversationModal
                role="admin"
                students={students}
                instructors={instructors}
                recipientType={recipientType}
                onRecipientTypeChange={setRecipientType}
                messageData={messageData}
                onMessageDataChange={setMessageData}
                announcementPriority={announcementPriority}
                onAnnouncementPriorityChange={setAnnouncementPriority}
                onSendBroadcast={handleSendBroadcast}
                sendingAnnouncement={sendingAnnouncement}
                isLoading={false}
              />
            </div>
          </div>
        </div>
      )}

      {/* ── Detail Modal (prebuilt) ── */}
      <AnnouncementDetailModal
        visible={!!selectedAnnouncement}
        onClose={() => setSelectedAnnouncement(null)}
        announcement={selectedAnnouncement}
      />

    </div>
  );
};

export default AdminMessaging;